import { useCallback, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import firebase from 'firebase/compat/app'
import map from 'lodash/map'

import { config } from '../../../../components/Init/Init'
import {
  arePurchaseNotificationProductsAvailable,
  isNotificationLimit,
  isNotificationPurchaseType,
  isNotificationTimePending,
  isStatusNotification
} from '../../../utils'
import { useTimePendingNotifications } from '../../../../hooks/casinoHooks'
import { useCasinoStore, usePlayerStore } from '../../../store'

export const useHandleFirebaseNotificationQueue = ({
  closeNotification,
  pendingNotifications,
  showNotificationPopup,
  setPendingNotifications,
  setPurchaseNotifications,
  removeNotificationFromFirebase
}) => {
  const playerStore = usePlayerStore()
  const playerProductsStore = playerStore.use.playerProductsStore()
  const playerLoginUser = playerStore.use.loginUser()

  const { setTimePendingNotifications } = useTimePendingNotifications(
    showNotificationPopup,
    removeNotificationFromFirebase
  )

  const casinoStore = useCasinoStore()
  const casinoActiveNotificationId = casinoStore.use.casinoActiveNotificationId()
  const casinoHoldNotifications = casinoStore.use.casinoHoldNotifications()

  // TODO: create hook to store actual value
  // needs to pass actual 'holdNotifications' value to avoid closure
  const casinoHoldNotificationsRef = useRef(casinoHoldNotifications)

  useEffect(() => {
    casinoHoldNotificationsRef.current = casinoHoldNotifications
  }, [casinoHoldNotifications])

  // needs to pass actual 'casinoActiveNotificationId' value to avoid closure of closeActiveNotificationIfNotInQueue
  const casinoActiveNotificationIdRef = useRef(casinoActiveNotificationId)

  useEffect(() => {
    casinoActiveNotificationIdRef.current = casinoActiveNotificationId
  }, [casinoActiveNotificationId])

  const moveNotificationToClaimCenter = useCallback(
    notificationId => {
      const moveNotification = firebase
        .functions()
        .httpsCallable('moveNotificationToClaimCenter' + config.firebaseCloudFunctionSuffix)

      return moveNotification({
        playerId: playerLoginUser.userId,
        notificationId: notificationId,
        firebaseDb: config.firebaseDb,
        useMessagingDb: config.firebaseUseMessagingDb
      })
    },
    [playerLoginUser.userId]
  )

  const closeActiveNotificationIfNotInQueue = useCallback(
    (notificationArray = []) => {
      const notificationsIds = map(notificationArray, 'id')
      if (casinoActiveNotificationIdRef.current && !notificationsIds.includes(casinoActiveNotificationIdRef.current)) {
        closeNotification(casinoActiveNotificationIdRef.current)
      }
    },
    [closeNotification]
  )

  const handleFirebaseNotificationQueue = useCallback(
    (db, playerId) => {
      db.ref(`users/${playerId}/notificationQueue`).on('value', snapshot => {
        let notificationArr = []
        if (snapshot.val() !== null) {
          notificationArr = snapshot.val()
        }
        try {
          closeActiveNotificationIfNotInQueue(notificationArr)

          if (config.popupsToBeDeleted) {
            if (config.enableRemoveNotice) {
              notificationArr
                .filter(n => config.popupsToBeDeleted.includes(n.type))
                .map(notification => {
                  removeNotificationFromFirebase(notification.id)
                  return notification
                })
            }
            notificationArr = notificationArr.filter(n => !config.popupsToBeDeleted.includes(n.type))
          }

          if (config.enableSweepstakes && config.enableClaimCenter && notificationArr.length > 0) {
            // move appropriate notifications to the claim center
            // (this won't be used once the claim center is established, but is needed on launch to port pending notifications over)
            for (let i = 0; i < notificationArr.length; i++) {
              let notification = notificationArr[i]
              if (Object.values(config.claimCenterNotificationTypes).indexOf(notification.type) >= 0) {
                // remove the notification from this local array and add it to our claim center data
                console.log('[Sweeps] Moving claim center notification from notification queue:', notification)
                moveNotificationToClaimCenter(notification.id)
                notificationArr.splice(i, 1)
                i--
              }
            }
          }

          if (!config.suppressNotificationPopups) {
            // scrub pending notifications
            if (notificationArr.length > 0) {
              const timePendingNotificationArr = []
              for (let i = 0; i < notificationArr.length; i++) {
                if (isNotificationTimePending(notificationArr[i])) {
                  // remove notification with time pending from the general queue
                  timePendingNotificationArr.push(notificationArr[i])
                  notificationArr.splice(i, 1)
                  i--
                }
              }
              // set notification with time pending to app state
              setTimePendingNotifications(timePendingNotificationArr)
            }

            // separate the purchase notifications which are not ready from the general queue, and deal with them in useEffect
            let purchaseNotificationArr = []
            for (let i = 0; i < notificationArr.length; i++) {
              if (
                isNotificationPurchaseType(notificationArr[i]) &&
                (config.enableCryptoWallet ||
                  !arePurchaseNotificationProductsAvailable(playerProductsStore, notificationArr[i]))
              ) {
                purchaseNotificationArr.push(notificationArr[i])
                notificationArr.splice(i, 1)
                i--
              }
            }
            setPurchaseNotifications(purchaseNotificationArr)

            const notifications = JSON.parse(JSON.stringify(notificationArr)) // copy notificationArr before we append pending

            if (snapshot.val() !== null) {
              if (pendingNotifications && pendingNotifications.length !== 0) {
                notificationArr = pendingNotifications.concat(notificationArr)
              }
            }
            if (notificationArr.length > 0) {
              if (casinoHoldNotificationsRef.current) {
                if (notifications.length > 0) {
                  let priorityArr = []
                  let pendingArr = []
                  // assuming we are not supposed to hold off status and limit notifications, then allow those through
                  while (notifications.length > 0) {
                    if (isNotificationLimit(notifications[0]) || isStatusNotification(notifications[0])) {
                      priorityArr.push(notifications.shift())
                    } else {
                      pendingArr.push(notifications.shift())
                    }
                  }
                  showNotificationPopup(priorityArr)

                  if (pendingNotifications && pendingNotifications.length !== 0) {
                    setPendingNotifications(pendingArr.concat(pendingNotifications))
                  } else {
                    setPendingNotifications(pendingArr)
                  }
                } else {
                  showNotificationPopup(notifications)
                }
              } else {
                showNotificationPopup(notificationArr)
                setPendingNotifications([])
              }
            } else {
              showNotificationPopup(notificationArr)
            }
          }
        } catch (e) {
          console.log('[onFirebaseNotificationQueueChange] There was an error processing this notification', e)
          if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
            Sentry.captureException(e)
          }
        }
      })
    },
    [
      playerProductsStore,
      pendingNotifications,
      showNotificationPopup,
      setPendingNotifications,
      setPurchaseNotifications,
      setTimePendingNotifications,
      moveNotificationToClaimCenter,
      removeNotificationFromFirebase,
      closeActiveNotificationIfNotInQueue
    ]
  )

  return { handleFirebaseNotificationQueue }
}
